import React, { FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useNavbar } from '../../../hooks/use-navbar';
import { useLivretReadAll } from '../../../hooks/use-livret-read';
import { faroLogger } from '../../../observability/faro-logger';

interface DocCard {
  key: string;
  to: string;
  /** Cle de stockage cote use-livret-read pour le ✓ "lu" */
  readKey: string;
  title: string;
  desc: string;
  icon: string;
  accent: string;
  accentBg: string;
}

const DOCS: DocCard[] = [
  {
    key: 'accueil',
    to: '/livret/consignes/accueil',
    readKey: 'consignes/accueil',
    title: 'Bienvenue chez Camion Net',
    desc: 'Mot du dirigeant, histoire et chiffres clés de l’entreprise.',
    icon: 'home',
    accent: '#31DCC8',
    accentBg: 'rgba(49,220,200,0.14)',
  },
  {
    key: 'contacts',
    to: '/livret/consignes/contacts',
    readKey: 'consignes/contacts',
    title: 'Contacts urgences',
    desc: 'Numéros d\'urgence : SAMU, pompiers, police, antipoison.',
    icon: 'phone',
    accent: '#3B82F6',
    accentBg: 'rgba(59,130,246,0.14)',
  },
  {
    key: 'pratiques',
    to: '/livret/consignes/pratiques',
    readKey: 'consignes/pratiques',
    title: 'Bonnes pratiques terrain',
    desc: 'Outils, badgeage, congés, intervention chez le client.',
    icon: 'check',
    accent: '#10B981',
    accentBg: 'rgba(16,185,129,0.14)',
  },
  {
    key: 'produits-pl99',
    to: '/livret/consignes/produits-pl99',
    readKey: 'consignes/produits-pl99',
    title: 'PL99 — Nettoyage extérieur & intérieur caisse «classique»',
    desc: 'Shampooing alcalin polyvalent ABAX — véhicules, caisses, autobus.',
    icon: 'flask',
    accent: '#F59E0B',
    accentBg: 'rgba(245,158,11,0.14)',
  },
  {
    key: 'produits-defiv',
    to: '/livret/consignes/produits-defiv',
    readKey: 'consignes/produits-defiv',
    title: 'DEFI-V — Nettoyant intérieur caisse frigorifique',
    desc: 'Désinfectant bactéricide, fongicide, virucide — SID France.',
    icon: 'flask',
    accent: '#3B82F6',
    accentBg: 'rgba(59,130,246,0.14)',
  },
  {
    key: 'reglement',
    to: '/livret/consignes/reglement',
    readKey: 'consignes/reglement',
    title: 'Règlement intérieur',
    desc: 'Règles d’hygiène, sécurité, discipline et fonctionnement.',
    icon: 'scroll',
    accent: '#8B5CF6',
    accentBg: 'rgba(139,92,246,0.14)',
  },
  {
    key: 'materiel',
    to: '/livret/consignes/materiel',
    readKey: 'consignes/materiel',
    title: 'Matériel & maintenance',
    desc: 'Nettoyeur HP : mise en route, pannes fréquentes, check sécu.',
    icon: 'wrench',
    accent: '#3B82F6',
    accentBg: 'rgba(59,130,246,0.14)',
  },
  {
    key: 'convention',
    to: '/livret/consignes/convention',
    readKey: 'consignes/convention',
    title: 'Convention collective',
    desc: 'Droits et obligations · entreprises de propreté (26/07/2011).',
    icon: 'book',
    accent: '#3B82F6',
    accentBg: 'rgba(59,130,246,0.14)',
  },
  {
    key: 'vehicule',
    to: '/livret/consignes/vehicule',
    readKey: 'consignes/vehicule',
    title: 'Véhicule & équipement',
    desc: 'Contrôles quotidiens, règles d’utilisation, anomalies.',
    icon: 'truck',
    accent: '#3B82F6',
    accentBg: 'rgba(59,130,246,0.14)',
  },
  {
    key: 'nettoyeurs',
    to: '/livret/nettoyeurs',
    readKey: 'nettoyeurs-hp',
    title: 'Procédure nettoyeurs haute pression',
    desc: 'Utilisation, entretien et ravitaillement (thermiques & électriques).',
    icon: 'shield',
    accent: '#FF8A95',
    accentBg: 'rgba(230,57,70,0.16)',
  },
];

const Icon: FC<{ name: string; size?: number; color?: string }> = ({
  name,
  size = 24,
  color = 'currentColor',
}) => {
  const p = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: color,
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (name) {
  case 'home':
    return (
      <svg {...p}>
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    );
  case 'phone':
    return (
      <svg {...p}>
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
      </svg>
    );
  case 'check':
    return (
      <svg {...p}>
        <polyline points="20 6 9 17 4 12" />
      </svg>
    );
  case 'flask':
    return (
      <svg {...p}>
        <path d="M9 3h6v6l5 9a2 2 0 0 1-2 3H6a2 2 0 0 1-2-3l5-9V3z" />
        <line x1="9" y1="3" x2="15" y2="3" />
      </svg>
    );
  case 'scroll':
    return (
      <svg {...p}>
        <path d="M8 21h12a2 2 0 0 0 2-2v-2H10v2a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v3h4" />
        <line x1="19" y1="3" x2="8" y2="3" />
      </svg>
    );
  case 'wrench':
    return (
      <svg {...p}>
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
      </svg>
    );
  case 'book':
    return (
      <svg {...p}>
        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
      </svg>
    );
  case 'truck':
    return (
      <svg {...p}>
        <path d="M10 17h4V5H2v12h3" />
        <path d="M20 17h2v-3.34a4 4 0 0 0-1.17-2.83L19 9h-5v8h1" />
        <circle cx="7.5" cy="17.5" r="2.5" />
        <circle cx="17.5" cy="17.5" r="2.5" />
      </svg>
    );
  case 'shield':
    return (
      <svg {...p}>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    );
  case 'arrow-right':
    return (
      <svg {...p} strokeWidth={2.2}>
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
      </svg>
    );
  default:
    return null;
  }
};

export const LivretDeBord: FC = () => {
  const navbar = useNavbar();
  const navigate = useNavigate();
  const { readSections } = useLivretReadAll();
  const totalSections = DOCS.length;
  const readCount = DOCS.filter((d) => readSections.has(d.readKey)).length;

  useEffect(() => {
    if (navbar) {
      navbar.setTitle('Livret de bord');
      navbar.setBackArrow(true);
      navbar.setOnBack(() => navigate('/'));
      navbar.setRight();
    }
    faroLogger.info('livret_hub_opened');
  }, [navbar, navigate]);

  return (
    <div
      style={{
        padding: '14px 14px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div
        style={{
          fontSize: 14.5,
          color: 'var(--text-secondary)',
          margin: '2px 4px 4px',
          lineHeight: 1.4,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 10,
        }}
      >
        <span>Tape sur un thème pour l&rsquo;ouvrir.</span>
        <span
          style={{
            fontSize: 12.5,
            fontWeight: 800,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: readCount === totalSections ? '#10B981' : 'var(--text-muted)',
            flexShrink: 0,
          }}
        >
          {readCount === totalSections ? '✓ Tout lu' : `${readCount}/${totalSections} lus`}
        </span>
      </div>

      {DOCS.map((d) => {
        const isRead = readSections.has(d.readKey);
        return (
          <button
            key={d.key}
            type="button"
            onClick={() => {
              faroLogger.info('livret_doc_opened', { key: d.key });
              navigate(d.to);
            }}
            style={{
              appearance: 'none',
              background: 'var(--bg-card)',
              border: `1px solid ${d.accent}55`,
              borderLeft: `4px solid ${d.accent}`,
              borderRadius: 12,
              padding: '12px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              cursor: 'pointer',
              textAlign: 'left',
              color: 'var(--text-primary)',
              fontFamily: 'inherit',
              width: '100%',
              WebkitTapHighlightColor: 'transparent',
              position: 'relative',
            }}
          >
            <span
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: d.accentBg,
                color: d.accent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                position: 'relative',
              }}
            >
              <Icon name={d.icon} size={22} color={d.accent} />
              {isRead && (
                <span
                  aria-label="Lu"
                  style={{
                    position: 'absolute',
                    bottom: -3,
                    right: -3,
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    background: '#10B981',
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 900,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid var(--bg-card)',
                  }}
                >
                  ✓
                </span>
              )}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 800,
                  color: 'var(--text-primary)',
                  letterSpacing: '-0.01em',
                  lineHeight: 1.2,
                  marginBottom: 4,
                }}
              >
                {d.title}
              </div>
              <div
                style={{
                  fontSize: 13.5,
                  color: 'var(--text-muted)',
                  lineHeight: 1.4,
                }}
              >
                {d.desc}
              </div>
            </span>
            <span style={{ flexShrink: 0, color: d.accent }}>
              <Icon name="arrow-right" size={18} color={d.accent} />
            </span>
          </button>
        );
      })}
    </div>
  );
};
